import type { JSX } from 'react';
import { css } from '@emotion/css';
import {
    generateState,
    Updater,
    useStore,
    useUpdate,
} from 'dg-web-shared/lib/Flux';
import {
    generateWriteStateSlice,
    RequestStatus,
    ServerRequestState,
} from 'dg-web-shared/lib/ServerRequestStateSlices';
import { request } from '../AsyncRequest';
import { enforcementPost } from '../Http';
import { texts } from '../i18n';
import { ResetPasswordForm } from './ResetPasswordForm';
import { slideInPortal, SlideInProps } from './SlideIn';
import { Spinner } from './Spinner';
import { ButtonContainer, TextButton } from './TextButton';
import { Colors, Typo } from './vars';
import { Language } from 'dg-web-shared/lib/Localized.ts';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';

// MODEL

type ResetPasswordFailure =
    | CommunicationError
    | NoEmailForUser
    | NoEmailForAdminUser;

interface CommunicationError {
    kind: RemoteRequestFailureKind.COMMUNICATION_ERROR;
    httpStatus: number;
}

interface NoEmailForUser {
    kind: RemoteRequestFailureKind.NO_EMAIL_FOR_USER;
    organizationAdminName: string;
}

interface NoEmailForAdminUser {
    kind: RemoteRequestFailureKind.NO_EMAIL_FOR_ADMIN_USER;
}

enum RemoteRequestFailureKind {
    COMMUNICATION_ERROR,
    NO_EMAIL_FOR_USER,
    NO_EMAIL_FOR_ADMIN_USER,
}

export type ResetPasswordResponse =
    | ResponseOk
    | ResponseNoEmailForUser
    | ResponseNoEmailForAdminUser;

interface ResponseOk {
    code: ResponseCode.OK;
}

interface ResponseNoEmailForUser {
    code: ResponseCode.NO_EMAIL_FOR_USER;
    organizationAdminName: string;
}

interface ResponseNoEmailForAdminUser {
    code: ResponseCode.NO_EMAIL_FOR_ADMIN_USER;
}

enum ResponseCode {
    OK = 'OK',
    NO_EMAIL_FOR_USER = 'NO_EMAIL_FOR_USER',
    NO_EMAIL_FOR_ADMIN_USER = 'NO_EMAIL_FOR_ADMIN_USER',
}

// VIEW

function ResetPasswordController(props: Props): JSX.Element {
    const { language } = useLanguage();
    switch (props.remoteRequest.status) {
        case RequestStatus.NEVER_EXECUTED:
            return (
                <ResetPasswordForm
                    suggestedUsername={props.suggestedUsername}
                    onCancel={props.onClose}
                    onSubmit={props.httpPostPasswordReset}
                    language={language}
                />
            );

        case RequestStatus.PENDING:
            return <Spinner />;

        case RequestStatus.ERROR:
            return (
                <Failure
                    username={props.submittedForm.username}
                    failure={{
                        kind: RemoteRequestFailureKind.COMMUNICATION_ERROR,
                        httpStatus: props.remoteRequest.httpStatus,
                    }}
                    onClose={props.onClose}
                    language={language}
                />
            );

        case RequestStatus.SUCCESS:
            if (props.remoteRequest.data.code === ResponseCode.OK) {
                return (
                    <Success
                        username={props.submittedForm.username}
                        onClose={props.onClose}
                        language={language}
                    />
                );
            }

            return (
                <Failure
                    username={props.submittedForm.username}
                    failure={failure(props.remoteRequest.data)}
                    onClose={props.onClose}
                    language={language}
                />
            );
    }
}

interface Props extends BaseProps {
    submittedForm: SubmittedFormState.State;
    remoteRequest: RemoteRequestState.State;
    httpPostPasswordReset: (username: string, turnstileToken: string) => void;
}

interface BaseProps extends SlideInProps {
    suggestedUsername: string;
}

function failure(
    res: ResponseNoEmailForUser | ResponseNoEmailForAdminUser,
): ResetPasswordFailure {
    if (res.code === ResponseCode.NO_EMAIL_FOR_USER) {
        return {
            kind: RemoteRequestFailureKind.NO_EMAIL_FOR_USER,
            organizationAdminName: res.organizationAdminName,
        };
    }

    return { kind: RemoteRequestFailureKind.NO_EMAIL_FOR_ADMIN_USER };
}

namespace SubmittedFormState {
    export interface State {
        username: string;
    }

    export const { get, stateWrite } = generateState<State>(
        'reset-password-controller-submitted-form',
        { username: '' },
    );
}

namespace RemoteRequestState {
    export type State = ServerRequestState<ResetPasswordResponse>;

    export const { get, setResponse, reset } =
        generateWriteStateSlice<ResetPasswordResponse>({
            key: 'reset-password-controller-remote-request',
        });
}

function ConnectedResetPasswordController({
    open,
    onClose,
    title,
    suggestedUsername,
}: {
    open: boolean;
    onClose: () => void;
    title: string | JSX.Element;
    suggestedUsername: string;
}) {
    const update = useUpdate();
    const { language } = useLanguage();
    const { storeState } = useStore(store => {
        return {
            submittedForm: SubmittedFormState.get(store),
            remoteRequest: RemoteRequestState.get(store),
        };
    });
    return (
        <ResetPasswordController
            open={open}
            onClose={() => {
                onClose();
                update(store => RemoteRequestState.reset(store));
            }}
            title={title}
            suggestedUsername={suggestedUsername}
            submittedForm={storeState.submittedForm}
            remoteRequest={storeState.remoteRequest}
            httpPostPasswordReset={makeHttpPostPasswordReset(update, language)}
        />
    );
}

function makeHttpPostPasswordReset(
    update: Updater,
    language: Language,
): (username: string, turnstileToken: string) => void {
    return (username, turnstileToken) => {
        update(store => SubmittedFormState.stateWrite(store, { username }));

        update(
            request({
                req: (data?: {
                    username: string;
                    language: Language;
                    source: 'APP';
                    turnstileToken: string;
                }) => enforcementPost('/reset-password', data!),

                useAuthToken: false,
                requestWrite: (store, res): string => {
                    RemoteRequestState.setResponse(store, res);
                    return 'reset-password';
                },
            }),
            {
                username,
                language: language,
                source: 'APP',
                turnstileToken,
            },
        );
    };
}

export const ResetPasswordControllerSlideIn = slideInPortal<BaseProps>(
    ConnectedResetPasswordController,
);

function Failure({
    username,
    failure,
    onClose,
    language,
}: {
    username: string;
    failure: ResetPasswordFailure;
    onClose: () => void;
    language: Language;
}) {
    return (
        <>
            <div className={errorStyle}>
                {failureMessage(username, failure, language)}
            </div>

            <Close onClick={onClose} language={language} />
        </>
    );
}

function failureMessage(
    username: string,
    failure: ResetPasswordFailure,
    language: Language,
): string {
    switch (failure.kind) {
        case RemoteRequestFailureKind.COMMUNICATION_ERROR:
            return texts[language].passwordResetRequestFailed(
                failure.httpStatus,
            );

        case RemoteRequestFailureKind.NO_EMAIL_FOR_USER:
            return texts[language].passwordResetRequestFailedNoEmailUser(
                username,
                failure.organizationAdminName,
            );

        case RemoteRequestFailureKind.NO_EMAIL_FOR_ADMIN_USER:
            return texts[language].passwordResetRequestFailedNoEmailAdmin(
                username,
            );
    }
}

function Success({
    username,
    onClose,
    language,
}: {
    username: string;
    onClose: () => void;
    language: Language;
}) {
    return (
        <>
            <div className={messageStyle}>
                {texts[language].passwordResetRequestSucceeded(username)}
            </div>

            <Close onClick={onClose} language={language} />
        </>
    );
}

function Close({
    onClick,
    language,
}: {
    onClick: () => void;
    language: Language;
}) {
    return (
        <ButtonContainer>
            <TextButton onClick={onClick} label={texts[language].close} />
        </ButtonContainer>
    );
}

const messageStyle = css`
    ${Typo.robotoLight};
    font-size: 20px;
    font-weight: bold;
    margin: 60px 30px 24px 30px;
`;

const errorStyle = css`
    color: ${Colors.red};
    ${messageStyle};
`;
